import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import type {
  AtbWeightDatabaseSettingsInput,
  AtbWeightDatabaseTestInput,
  AtbWeightDatabaseTestResult,
  AtbWeightDatabaseView,
} from '@reform-lite/contracts';
import { apiClient } from '@/lib/api-client';

/**
 * ATBL-449 — a store's one weight database (lane SQLite or WinSCOServer on
 * Microsoft SQL). The password goes out with a save or a test and never comes
 * back: the view says only `passwordSet`.
 */
const key = (locationId: string) => ['atb-weight-database', locationId] as const;
const path = (locationId: string) =>
  `/weight/atb-database/stores/${encodeURIComponent(locationId)}`;

export function useAtbWeightDatabase(locationId: string, enabled: boolean) {
  return useQuery({
    queryKey: key(locationId),
    enabled,
    // The status follows the refresh job, which runs every minute.
    refetchInterval: 30_000,
    queryFn: async (): Promise<AtbWeightDatabaseView> =>
      (await apiClient.get<AtbWeightDatabaseView>(path(locationId))).data,
  });
}

export function useSaveAtbWeightDatabase(locationId: string) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (input: AtbWeightDatabaseSettingsInput): Promise<AtbWeightDatabaseView> =>
      (await apiClient.put<AtbWeightDatabaseView>(path(locationId), input)).data,
    onSuccess: (view) => queryClient.setQueryData(key(locationId), view),
  });
}

export function useTestAtbWeightDatabase(locationId: string) {
  return useMutation({
    mutationFn: async (input: AtbWeightDatabaseTestInput): Promise<AtbWeightDatabaseTestResult> =>
      (await apiClient.post<AtbWeightDatabaseTestResult>(`${path(locationId)}/test`, input)).data,
  });
}

export function useClearAtbWeightDatabasePassword(locationId: string) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (): Promise<AtbWeightDatabaseView> =>
      (await apiClient.delete<AtbWeightDatabaseView>(`${path(locationId)}/password`)).data,
    onSuccess: (view) => queryClient.setQueryData(key(locationId), view),
  });
}
